// components
import { Button, Table, Tooltip, Modal, Input } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { Head } from "components/molecules/Head/Head";
import { useOfferIssuanceApprove } from "./useOfferIssuanceApprove";
import Spinner from "components/atoms/Spinner/Spinner";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import RetroClaimFilters from "components/molecules/RetroClaimFilters/RetroClaimFilters";

// styles
import styles from "./OfferIssuanceApprove.module.css";

// constants
import { PageTitles } from "constants/global";
import moment from "moment";

const { TextArea } = Input;

const OfferIssuanceApprovePage = () => {
	const {
		loading,
		draftList,
		handleCreateOffer,
		handlePagination,
		currentPage,
		pageSize,
		getActionButtons,
		showFilters,
		toggleFilters,
		handleFilterChange,
		selectedFilters,
		handleFilterReset,
		modalVisible,
		modalAction,
		modalLoading,
		handleModalOk,
		handleModalCancel,
		rejectReason,
		rejectReasonError,
		handleRejectReasonChange,
		selectedRecord,
		hasCreatePermission
	} = useOfferIssuanceApprove();

	const getModalButtonText = (action: string | null) => {
		if (action === "approve") return "Approve";
		if (action === "reject") return "Reject";
		return "Cancel Request";
	};

	const getModalButtonClass = (action: string | null) => {
		if (action === "approve") return styles.approve_button;
		if (action === "reject") return styles.reject_button;
		return styles.cancel_button;
	};

	const getFilterButtonClass = (isActive: boolean) => {
		return `${styles.filter_button} ${isActive ? styles.filter_button_active : ""}`;
	};

	const renderOfferName = (text: string | null, record: any) => {
		const safeText = String(text || record.offer_id || "");
		return (
			<Tooltip title={safeText}>
				<div className="truncate">{safeText}</div>
			</Tooltip>
		);
	};

	const renderDate = (date: string | null) => (date ? moment(date).format("DD MMM YY") : "-");

	const renderTooltipText = (text: string | null) => {
		if (!text) return "-";
		const safeText = String(text);
		return (
			<Tooltip title={safeText}>
				<div className="truncate">{safeText}</div>
			</Tooltip>
		);
	};

	const getModalTitle = () => {
		switch (modalAction) {
			case "approve":
				return "Approve Draft";
			case "reject":
				return "Reject Draft";
			case "cancel":
				return "Cancel Draft";
			default:
				return "";
		}
	};

	const getModalContent = () => {
		if (!selectedRecord) return "";

		switch (modalAction) {
			case "approve":
				return `Are you sure you want to Approve Draft ID: ${selectedRecord.id}?`;
			case "reject":
				return (
					<div className="space-y-4">
						<b>Are you sure you want to Reject Draft ID: {selectedRecord.id}?</b>
						<p>Please provide a reason for rejecting this offer:</p>
						<TextArea
							placeholder="Enter rejection reason"
							value={rejectReason}
							onChange={(e) => handleRejectReasonChange(e.target.value)}
							status={rejectReasonError ? "error" : ""}
							rows={4}
						/>
						{rejectReasonError && <p className="text-red-500 text-sm">Rejection reason is required</p>}
					</div>
				);
			case "cancel":
				return `Are you sure you want to Cancel Draft ID: ${selectedRecord.id}?`;
			default:
				return "";
		}
	};

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
			width: 50
		},
		{
			title: "Created At",
			dataIndex: "created_at",
			key: "created_at",
			width: 130,
			render: (date: string) => moment(date).format("DD MMM YY HH:mm:ss")
		},
		{
			title: "Created By",
			dataIndex: "created_by",
			key: "created_by",
			width: 180,
			ellipsis: true,
			render: renderTooltipText
		},
		{
			title: "Member Type",
			dataIndex: "member_type",
			key: "member_type",
			width: 180,
			render: (type: string) => type.replace("_", " ").toUpperCase()
		},
		{
			title: "Offer Name",
			dataIndex: "voucher_type_name",
			key: "voucher_type_name",
			width: 250,
			ellipsis: true,
			render: renderOfferName
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			width: 100,
			align: "center" as const
		},
		{
			title: "Total amount",
			dataIndex: "total_amount",
			key: "total_amount",
			width: 150,
			align: "center" as const,
			render: (amount: string | null) => (amount === null ? "$0" : `$${amount}`)
		},
		{
			title: "Start Date",
			dataIndex: "start_date",
			key: "start_date",
			width: 120,
			render: renderDate
		},
		{
			title: "End Date",
			dataIndex: "end_date",
			key: "end_date",
			width: 120,
			render: renderDate
		},
		{
			title: "Remarks",
			dataIndex: "remarks",
			key: "remarks",
			width: 250,
			render: renderTooltipText
		},
		{
			title: "Reject Reason",
			dataIndex: "reject_reason",
			key: "reject_reason",
			width: 250,
			render: renderTooltipText
		},
		{
			title: "Updated At",
			dataIndex: "updated_at",
			key: "updated_at",
			width: 120,
			render: (date: string) => moment(date).format("DD MMM YY HH:mm:ss")
		},
		{
			title: "Updated By",
			dataIndex: "updated_by",
			key: "updated_by",
			width: 180,
			ellipsis: true
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 120,
			render: (status: string) => <span className={`status-${status.toLowerCase()}`}>{status.replace("_", " ").toUpperCase()}</span>
		},
		{
			title: "Actions",
			key: "actions",
			width: 220,
			fixed: "right" as const,
			render: (record: any) => getActionButtons(record)
		}
	];

	const filterConfig = [
		{
			title: "Filter by",
			values: [
				{
					value: "pending",
					label: "Pending",
					id: "status-1"
				},
				{
					value: "approved",
					label: "Approved",
					id: "status-2"
				},
				{
					value: "rejected",
					label: "Rejected",
					id: "status-3"
				},
				{
					value: "processing",
					label: "Processing",
					id: "status-4"
				},
				{
					value: "completed",
					label: "Completed",
					id: "status-5"
				},
				{
					value: "failed",
					label: "Failed",
					id: "status-6"
				}
			]
		},
		{
			title: "Sort by",
			values: [
				{
					value: "newest",
					label: "Newest to Oldest",
					id: "status-4"
				},
				{
					value: "oldest",
					label: "Oldest to Newest",
					id: "status-5"
				}
			]
		}
	];

	return (
		<div className="min-h-screen relative">
			{loading && <Spinner type="transparent" />}
			<div className={styles.root}>
				<Head>{PageTitles.OFFER_ISSUANCE_APPROVE}</Head>
				<div className={styles.header}>
					<PageTitle title="Offer Issuance Approval" subTitle="Allow user to view/approve/reject/download draft offer issuances" />
					<div className={`${styles.button_section} ${!hasCreatePermission ? styles.single_button : ""}`}>
						{hasCreatePermission && (
							<Button type="primary" className={styles.issue_button} onClick={handleCreateOffer}>
								Issue Offers
							</Button>
						)}
						<Button className={getFilterButtonClass(showFilters)} onClick={toggleFilters} icon={<FilterOutlined />}>
							Filter
						</Button>
					</div>
				</div>

				{showFilters && (
					<div className={styles.filters_container}>
						<RetroClaimFilters
							filters={filterConfig}
							selectedFilers={[selectedFilters.join(","), selectedFilters.find((f) => ["newest", "oldest"].includes(f))]}
							onClick={handleFilterChange}
							onReset={handleFilterReset}
						/>
					</div>
				)}

				<div className={styles.table_container}>
					<Table
						columns={columns}
						dataSource={draftList?.data?.result || []}
						rowKey="id"
						pagination={{
							current: currentPage,
							pageSize: pageSize,
							total: draftList?.data?.total_count || 0,
							onChange: handlePagination,
							showSizeChanger: false
						}}
						scroll={{ x: 1620 }}
						className={styles.table}
					/>
				</div>

				<Modal
					title={getModalTitle()}
					open={modalVisible}
					onOk={handleModalOk}
					onCancel={handleModalCancel}
					confirmLoading={modalLoading}
					okText={getModalButtonText(modalAction)}
					cancelText="Cancel"
					okButtonProps={{
						className: getModalButtonClass(modalAction)
					}}
					cancelButtonProps={{
						className: "ant-btn ant-btn-default"
					}}
					width={modalAction === "reject" ? 600 : 520}
					footer={[
						<Button key="cancel" onClick={handleModalCancel}>
							Cancel
						</Button>,
						<Button key="submit" type="primary" loading={modalLoading} onClick={handleModalOk} className={getModalButtonClass(modalAction)}>
							{getModalButtonText(modalAction)}
						</Button>
					]}
				>
					{getModalContent()}
				</Modal>
			</div>
		</div>
	);
};

export default OfferIssuanceApprovePage;
